import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChatStore } from "../store/useChatStore";

const TOTP_APP_NAME = "Regulify";

const Login = () => {
  const [form, setForm] = useState({ username: "", password: "" });
  const [mfaCode, setMfaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authPhase, setAuthPhase] = useState("password");
  const [totpSetupUri, setTotpSetupUri] = useState("");
  const [mfaHint, setMfaHint] = useState("");
  const [allowedMfaTypes, setAllowedMfaTypes] = useState([]);
  const navigate = useNavigate();
  const resetChatMessages = useChatStore((state) => state.resetChatMessages);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
        navigate("/");
      } catch {
        // stay on login
      }
    };

    checkAuth();
  }, [navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const finalizeSession = async () => {
    const session = await fetchAuthSession();
    const sub = session.tokens.idToken.payload.sub;
    const email_id = session.tokens.idToken.payload.email;
    sessionStorage.setItem("session_id", sub);
    sessionStorage.setItem("email_id", email_id);
    navigate("/");
  };

  const applySignInResult = async (result) => {
    if (result.isSignedIn) {
      await finalizeSession();
      return;
    }

    const step = result.nextStep?.signInStep;
    switch (step) {
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        setAuthPhase("mfaCode");
        setMfaHint("Enter the 6-digit code from your authenticator app.");
        setMfaCode("");
        break;
      case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
      case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE": {
        setAuthPhase("mfaCode");
        const dest = result.nextStep?.codeDeliveryDetails?.destination;
        const medium = result.nextStep?.codeDeliveryDetails?.deliveryMedium;
        setMfaHint(
          dest
            ? `Enter the code sent to ${dest} (${medium || "verification"}).`
            : "Enter the verification code sent to you.",
        );
        setMfaCode("");
        break;
      }
      case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
        const details = result.nextStep?.totpSetupDetails;
        const uri = details?.getSetupUri?.(TOTP_APP_NAME, form.username.trim());
        setTotpSetupUri(typeof uri === "string" ? uri : uri?.href ?? "");
        setAuthPhase("totpSetup");
        setMfaHint(
          "Scan the QR code or open the setup link in Google Authenticator, Microsoft Authenticator, or another TOTP app. Then enter the 6-digit code.",
        );
        setMfaCode("");
        break;
      }
      case "CONTINUE_SIGN_IN_WITH_MFA_SELECTION": {
        setAuthPhase("mfaSelect");
        setAllowedMfaTypes(result.nextStep?.allowedMFATypes ?? []);
        setMfaHint("Choose how you want to verify.");
        break;
      }
      case "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION": {
        setAuthPhase("mfaSetupSelect");
        setAllowedMfaTypes(result.nextStep?.allowedMFATypes ?? []);
        setMfaHint("Choose how to set up multi-factor authentication.");
        break;
      }
      case "CONTINUE_SIGN_IN_WITH_EMAIL_SETUP":
        setAuthPhase("mfaCode");
        setMfaHint("Follow the email MFA setup instructions from Cognito, or contact your administrator.");
        break;
      case "RESET_PASSWORD":
        setError("You must reset your password before signing in.");
        setAuthPhase("password");
        break;
      case "CONFIRM_SIGN_UP":
        setError("Please confirm your email address before signing in. Check your inbox or register again.");
        setAuthPhase("password");
        break;
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        setError("A new password is required for this account. Use Forgot password or contact your administrator.");
        setAuthPhase("password");
        break;
      default:
        setError(
          step
            ? `Additional sign-in step is required (${step}) but is not handled in this app.`
            : "Sign-in could not be completed.",
        );
    }
  };

  const handleAbandonChallenge = async () => {
    try {
      await signOut();
    } catch {
      /* ignore */
    }
    setAuthPhase("password");
    setMfaCode("");
    setTotpSetupUri("");
    setMfaHint("");
    setAllowedMfaTypes([]);
    setError("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      try {
        await getCurrentUser();
        navigate("/");
        return;
      } catch {
        // continue
      }

      const result = await signIn({
        username: form.username.trim(),
        password: form.password,
      });
      await applySignInResult(result);
    } catch (err) {
      console.error("Login failed:", err);
      setError(err.message || "Invalid username or password.");
    } finally {
      resetChatMessages();
      setLoading(false);
    }
  };

  const handleConfirmSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const result = await confirmSignIn({ challengeResponse: mfaCode.trim() });
      resetChatMessages();
      await applySignInResult(result);
    } catch (err) {
      console.error("Confirm sign-in failed:", err);
      setError(err.message || "Verification failed.");
    } finally {
      resetChatMessages();
      setLoading(false);
    }
  };

  const handleSelectMfa = async (mfaType) => {
    setLoading(true);
    setError("");
    try {
      const result = await confirmSignIn({ challengeResponse: mfaType });
      resetChatMessages();
      await applySignInResult(result);
    } catch (err) {
      console.error("MFA selection failed:", err);
      setError(err.message || "Could not continue sign-in.");
    } finally {
      resetChatMessages();
      setLoading(false);
    }
  };

  const showPasswordForm = authPhase === "password";
  const showMfaCodeForm = authPhase === "mfaCode" || authPhase === "totpSetup";
  const showMfaSelect = authPhase === "mfaSelect";
  const showMfaSetupSelect = authPhase === "mfaSetupSelect";

  return (
    <div className="bg-base-200 flex min-h-screen items-center justify-center px-4">
      <div className="card bg-base-100 w-full max-w-md shadow-xl">
        <div className="card-body">
          <h2 className="card-title justify-center text-2xl">Login</h2>

          {error && <div className="alert alert-error text-sm">{error}</div>}

          {showPasswordForm && (
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="email"
                name="username"
                placeholder="Email"
                className="input input-bordered w-full"
                onChange={handleChange}
                value={form.username}
                required
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                className="input input-bordered w-full"
                onChange={handleChange}
                value={form.password}
                required
              />
              <div className="card-actions mt-4">
                <button className="btn btn-primary w-full" disabled={loading} type="submit">
                  {loading ? "Logging in..." : "Login"}
                </button>
              </div>
            </form>
          )}

          {showMfaSelect && (
            <div className="space-y-4">
              {mfaHint && <p className="text-sm opacity-80">{mfaHint}</p>}
              <div className="flex flex-col gap-2">
                {allowedMfaTypes.length === 0 ? (
                  <p className="text-sm text-error">No verification methods available. Try cancel and sign in again.</p>
                ) : (
                  allowedMfaTypes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="btn btn-outline"
                      disabled={loading}
                      onClick={() => handleSelectMfa(t)}
                    >
                      {t === "TOTP" && "Authenticator app"}
                      {t === "SMS" && "Text message (SMS)"}
                      {t === "EMAIL" && "Email"}
                      {!["TOTP", "SMS", "EMAIL"].includes(t) && t}
                    </button>
                  ))
                )}
              </div>
              <button type="button" className="btn btn-ghost btn-sm w-full" onClick={handleAbandonChallenge}>
                Cancel
              </button>
            </div>
          )}

          {showMfaSetupSelect && (
            <div className="space-y-4">
              {mfaHint && <p className="text-sm opacity-80">{mfaHint}</p>}
              <div className="flex flex-col gap-2">
                {allowedMfaTypes.length === 0 ? (
                  <p className="text-sm text-error">No MFA setup options available. Try cancel and sign in again.</p>
                ) : (
                  allowedMfaTypes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="btn btn-outline"
                      disabled={loading}
                      onClick={() => handleSelectMfa(t)}
                    >
                      {t === "TOTP" && "Set up authenticator app"}
                      {t === "EMAIL" && "Set up email MFA"}
                      {!["TOTP", "EMAIL"].includes(t) && t}
                    </button>
                  ))
                )}
              </div>
              <button type="button" className="btn btn-ghost btn-sm w-full" onClick={handleAbandonChallenge}>
                Cancel
              </button>
            </div>
          )}

          {showMfaCodeForm && (
            <form onSubmit={handleConfirmSignIn} className="space-y-4">
              {mfaHint && <p className="text-sm opacity-80">{mfaHint}</p>}
              {authPhase === "totpSetup" && totpSetupUri && (
                <div className="space-y-2 rounded-lg border border-base-300 p-3">
                  <a href={totpSetupUri} className="link link-primary break-all text-sm" target="_blank" rel="noreferrer">
                    Open authenticator setup link
                  </a>
                  <p className="text-xs opacity-70">
                    If your device does not open the link, add an account manually using the secret from your administrator
                    or use the link on a phone where your authenticator app is installed.
                  </p>
                </div>
              )}
              <input
                type="text"
                name="mfaCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
                className="input input-bordered w-full"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
              />
              <button className="btn btn-primary w-full" disabled={loading} type="submit">
                {loading ? "Verifying..." : "Continue"}
              </button>
              <button type="button" className="btn btn-ghost btn-sm w-full" onClick={handleAbandonChallenge}>
                Cancel
              </button>
            </form>
          )}

          {showPasswordForm && (
            <p className="mt-2 text-center text-sm">
              <a href="/forgot-password" className="link link-primary">
                Forgot password?
              </a>
            </p>
          )}

          <p className="mt-2 text-center text-sm">
            Don&apos;t have an account?{" "}
            <a href="/register" className="link link-primary">
              Register
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
